// 전역 자동 재구독 — 구독이 조용히 사라졌을 때 되살리고, 살아 있으면 서버 행이 아직 있는지 가끔 재확인한다.
//
// 같은 판정이 MyPage(내 정보)에도 있지만 그 화면에 들어가야만 돌았다. 앱 어디에 있든 돌도록 App 에서
// 마운트한다(MyPage 와 중복 실행돼도 /subscribe 는 endpoint upsert 라 무해).
//
// 🔴 권한이 default 면 아무것도 하지 않는다 — 구독도, 권한 묻기도. 페이지 로드에서 권한을 물으면
//    브라우저가 무시하거나 자동 거부한다(사용자 제스처가 필요하다). 권한 받기는 PushSetupBanner 의
//    버튼이 맡는다.
// 🔴 App 은 <BrowserRouter> **밖**이라 useLocation 을 쓸 수 없다. SPA 로그인은 리로드가 없어 App 이
//    재마운트되지 않으므로, 마운트 1회로 끝내면 로그인한 세션에서는 영영 돌지 않는다 → 토큰을 폴링해
//    로그인 직후에도 한 번 재평가한다.
// 🔴 로컬 구독이 있어도 그대로 넘기지 않는다 — SW 의 rotate 가 실패했거나 발송 410 으로 서버 행이
//    지워졌으면 「브라우저엔 구독이 있는데 서버엔 없는」 상태가 되고, 그때 넘기면 영영 끊긴다.
import { useEffect } from 'react';
import { authAPI, pushAPI } from '../api/client';
import {
  dropForeignGatewaySubscriptions, isPushSupported, notificationPermission, subscribeCurrentBrowser, swReady,
} from '../utils/push';

const POLL_MS = 30000;                       // 로그인 직후 재평가까지의 지연 상한
const RECHECK_MS = 24 * 60 * 60 * 1000;      // 서버 행 재확인 주기(로컬 구독이 살아 있을 때)
const FAIL_BACKOFF_MS = 5 * 60 * 1000;       // 실패가 이어질 때 서버를 계속 두드리지 않도록
const CHECK_KEY = 'gwPushServerCheckedAt';

function readToken() {
  try { return localStorage.getItem('token') || ''; } catch (e) { return ''; }
}

function serverCheckDue() {
  try {
    const t = parseInt(localStorage.getItem(CHECK_KEY) || '0', 10);
    return !(t > 0) || (Date.now() - t) > RECHECK_MS;
  } catch (e) { return true; }
}

function markServerChecked() {
  try { localStorage.setItem(CHECK_KEY, String(Date.now())); } catch (e) { /* */ }
}

export default function usePushAutoSubscribe() {
  useEffect(() => {
    if (!isPushSupported()) return undefined;

    let dead = false;
    let running = false;
    let lastToken = null;      // null = 아직 한 번도 평가하지 않음
    let nextAttempt = 0;

    const ensure = async () => {
      if (notificationPermission() !== 'granted') return;   // 🔴 default·denied 는 건드리지 않는다
      const reg = await swReady();
      if (!reg || dead) return;
      await dropForeignGatewaySubscriptions();   // 루트 밖 등록에 붙은 게이트웨이 구독을 끊는다(10-30 #75 · 결과 무시)
      if (dead) return;
      const sub = await reg.pushManager.getSubscription();
      if (dead) return;
      const me = await authAPI.getMe();
      if (dead) return;
      // 알림 사용 의사(users.notifications_enabled)가 참일 때만 — 사용자가 끈 걸 되살리지 않는다
      if (!me || !me.data || !me.data.notifications_enabled) { markServerChecked(); return; }
      if (sub) await pushAPI.subscribe(sub.toJSON());   // 서버 행 재확인(endpoint upsert 라 무해)
      else await subscribeCurrentBrowser();
      markServerChecked();
    };

    const tick = () => {
      if (dead || running) return;
      const token = readToken();
      const changed = token !== lastToken;
      lastToken = token;
      if (!token) return;                                   // 비로그인 화면에선 움직이지 않는다
      // 토큰이 바뀌었으면(=방금 로그인) 무조건 본다. 아니면 백오프·재확인 주기를 지킨다 —
      // 할 일이 없을 땐 브라우저 API 도 건드리지 않는다.
      if (!changed && (Date.now() < nextAttempt || !serverCheckDue())) return;
      running = true;
      ensure()
        .then(() => { nextAttempt = 0; })
        .catch(() => { nextAttempt = Date.now() + FAIL_BACKOFF_MS; })
        .then(() => { running = false; });
    };

    tick();
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };   // 배경 탭은 타이머가 조여진다
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      dead = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
